import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useLanguage } from "../../context/LanguageContext";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import { useNavigate } from "react-router-dom";
import { formatPrice } from "../../utils/functions.js";
import { Heart } from "lucide-react";
import placeholder from "../../assets/placeholder.svg";
import api from "../../lib/api.ts";


const WishList = () => {
  const { translate } = useLanguage();
  const { user } = useAuth();
  const { showSuccess, showError } = useToast();
  const navigate = useNavigate();
  
  // State management
  const [wishlistItems, setWishlistItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 10,
    totalPages: 0,
    hasNext: false,
    hasPrev: false
  });
  const [sortBy, setSortBy] = useState("auction"); // "auction", "runNo", "laneNo"
  const [sortOrder, setSortOrder] = useState("asc"); // "asc", "desc"
  const [notificationCount, setNotificationCount] = useState(0);

  useEffect(() => {
    if (user?.id) {
      fetchNotificationCount();
    }
  }, [user?.id]);
  
  const fetchNotificationCount = async () => {
    try {
      const response = await api.get(`/notifications/getusernotifications`, {
        params: {
          userId: user?.id
        }
      });
      console.log("response",response.data)
      setNotificationCount(response.data.data?.length || 0);
    } catch (error) {
      console.error("Error fetching notification count:", error);
      setNotificationCount(0);
    }
  };



  // Sorting function
  const sortWishlistItems = (items) => {
    if (!items || items.length === 0) return items;
    
    return [...items].sort((a, b) => {
      let aValue, bValue;
      
      switch (sortBy) {
        case "auction":
          aValue = a?.vehicle?.auctionLanesVehicles?.[0]?.auctionLane?.auction?.name || "";
          bValue = b?.vehicle?.auctionLanesVehicles?.[0]?.auctionLane?.auction?.name || "";
          break;
        case "runNo":
          aValue = a?.vehicle?.auctionLanesVehicles?.[0]?.runNo || 0;
          bValue = b?.vehicle?.auctionLanesVehicles?.[0]?.runNo || 0;
          break;
        case "laneNo":
          aValue = a?.vehicle?.auctionLanesVehicles?.[0]?.auctionLane?.lane?.name || "";
          bValue = b?.vehicle?.auctionLanesVehicles?.[0]?.auctionLane?.lane?.name || "";
          break;
        default:
          return 0;
      }
      
      // Handle numeric comparison for runNo
      if (sortBy === "runNo") {
        const aNum = Number(aValue) || 0;
        const bNum = Number(bValue) || 0;
        return sortOrder === "asc" ? aNum - bNum : bNum - aNum;
      }
      
      // Handle string comparison for auction and laneNo
      if (sortOrder === "asc") {
        return aValue.localeCompare(bValue);
      } else {
        return bValue.localeCompare(aValue);
      }
    });
  };

  // Handle sort change
  const handleSortChange = (newSortBy) => {
    if (sortBy === newSortBy) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(newSortBy);
      setSortOrder("asc");
    }
  };

  // Fetch wishlist items
  const fetchWishlistItems = async () => {
    if (!user?.id) return;

    try {
      setLoading(true);
      setError("");
      
      // Call API with pagination parameters
      const response = await api.get(`/wishlist`, {
        params: {
          page: currentPage,
          limit: pageSize
        }
      });
      
      if (response.data.success) {
        setWishlistItems(response.data.data || []);
        // Set pagination data from API response
        if (response.data.pagination) {
          setPagination(response.data.pagination);
        }
      } else {
        setWishlistItems([]);
        setPagination({
          total: 0,
          page: 1,
          limit: 10,
          totalPages: 0,
          hasNext: false,
          hasPrev: false
        });
      }
    } catch (err) {
      console.error("Error fetching wishlist:", err);
      setError(translate("wishList.messages.loadError") || "Failed to load Watch List items. Please try again.");
      setWishlistItems([]);
      setPagination({
        total: 0,
        page: 1,
        limit: 10,
        totalPages: 0,
        hasNext: false,
        hasPrev: false
      });
    } finally {
      setLoading(false);
    }
  };


  // Remove item from wishlist
  const removeFromWishlist = async (vehicleId) => {
    try {
      const response = await api.post(`/wishlist/toggle`, { vehicleId });
      
      if (response.data.success) {
        setWishlistItems(prev => 
          prev.filter(item => item.vehicle?.id !== vehicleId)
        );
        showSuccess(translate("wishList.messages.removed") || translate("vehicleCard.messages.wishlistRemoved"));
      } else {
        showError(translate("wishList.messages.removeError") || translate("vehicleCard.messages.wishlistFailed"));
      }
    } catch (err) {
      console.error("Error removing from Watch List:", err);
      showError(translate("wishList.messages.removeError") || translate("vehicleCard.messages.wishlistFailed"));
    }
  };



  

  // Get all wishlist items (no filtering)
  const getAllItems = () => {
    
    return sortWishlistItems(wishlistItems);
  };

  // Get auction status
  const getAuctionStatus = (item) => {
    const startDate = new Date(item.auction?.startDate);
    const endDate = new Date(item.auction?.endDate);
    const now = new Date();

    if (now < startDate) return "upcoming";
    if (now >= startDate && now <= endDate) return "live";
    return "ended";
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case "upcoming": return "bg-blue-100 text-blue-800";
      case "live": return "bg-red-100 text-red-800";
      case "ended": return "bg-gray-100 text-gray-800";
      default: return "bg-gray-100 text-gray-800";
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  // Load data on component mount and when page changes
  useEffect(() => {
    fetchWishlistItems();
  }, [user?.id, currentPage]);

  // Reset to first page when sorting changes
  useEffect(() => {
    setCurrentPage(1);
  }, [sortBy, sortOrder]);

  // Pagination - using server-side pagination
  const allItems = getAllItems();
  const totalPages = pagination.totalPages || 1;
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedItems = allItems; // Items are already paginated from server

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <>

      <motion.div
        className="bg-white rounded-md shadow overflow-x-auto mx-1 md:mx-0"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 20 }}
        transition={{ duration: 0.5 }}
      >
      {/* Header */}
      <div className="bg-[#fcfcfc1f] text-gray-700 font-medium rounded-t-[16px] px-1 py-1 border-b border-[#cccccc]">
        <div className="p-2 sm:p-3 text-base rounded-t-[12px] font-semibold text-gray-700 bg-[#F153291F] border-b border-black pb-2">
          <div className="flex items-center justify-between">
            <h2 className="text-sm sm:text-base lg:text-lg font-semibold flex items-center">
              <Heart className="w-5 h-5 mr-2 text-red-500" />
              {translate("wishList.heading") || "My Watch List"}
            </h2>
            <div className="flex items-center gap-3">
              <div className="text-sm font-bold text-black">
                {pagination.total} {pagination.total === 1 ? translate("wishList.pagination.items") : translate("wishList.pagination.items")}
              </div>
              {/* Sorting Dropdown */}
              <div className="relative">
                <select
                  value={`${sortBy}-${sortOrder}`}
                  onChange={(e) => {
                    const [newSortBy, newSortOrder] = e.target.value.split('-');
                    setSortBy(newSortBy);
                    setSortOrder(newSortOrder);
                  }}
                  className="text-xs bg-white border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                >
                  <option value="auction-asc">Auction A-Z</option>
                  <option value="auction-desc">Auction Z-A</option>
                  <option value="runNo-asc">Run No ↑</option>
                  <option value="runNo-desc">Run No ↓</option>
                  <option value="laneNo-asc">Lane A-Z</option>
                  <option value="laneNo-desc">Lane Z-A</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="p-2 md:p-2">


        {/* Error State */}
        {error && (
          <div className="text-center py-8 text-red-600 bg-red-50 rounded-md mb-4">
            {error}
          </div>
        )}

        {/* Empty State */}
        {!loading && !error && allItems.length === 0 && (
          <div className="text-center py-12">
            <Heart className="mx-auto h-16 w-16 text-gray-300 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {translate("wishList.emptyState.title")}
            </h3>
            <p className="text-gray-500 mb-4">
              {translate("wishList.emptyState.description")}
            </p>
            <button
              onClick={() => navigate("/vehicles")}
              className="bg-orange-500 text-white px-6 py-2 rounded-md hover:bg-orange-600 transition-colors"
            >
              {translate("wishList.emptyState.browseButton")}
            </button>
          </div>
        )}

        {/* Wishlist List View */}
        {allItems.length > 0 && (
          <>
            <div className="space-y-2">
              {paginatedItems.map((item) => {
                const status = getAuctionStatus(item);
                const vehicle = item.vehicle;
                
                return (

<>
<div>
  <sellerListView vehicle={vehicle} />
</div>

                  
                  <div key={item.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-3">
                    <div className="flex flex-col lg:flex-row lg:items-center space-y-2 lg:space-y-0 lg:space-x-4">
                      {/* Vehicle Image */}
                      <div className="flex-shrink-0">
                        <img
                          src={
                            (vehicle?.images && vehicle?.images?.length > 0 && vehicle?.images[0]?.url) ||
                            vehicle?.images?.[0]?.imageUrl ||
                            placeholder
                          }
                          alt={vehicle?.name}
                          className="w-full lg:w-24 h-32 lg:h-20 object-cover rounded-lg"
                        />
                      </div>

                      {/* Vehicle Details */}
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-2 lg:space-y-0">
                          <div className="flex-1">
                            <h3 className="text-base font-semibold text-gray-900 mb-1">
                              {vehicle?.name}
                            </h3>
                            
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2 text-xs">
                              <div>
                                <span className="text-gray-500 block">{translate("wishList.vehicle.lotNumber")}</span>
                                <span className="font-medium text-gray-900">
                                  {vehicle?.lotNumber}
                                </span>
                                <span className="text-gray-500 block">{translate("vehicleCard.labels.vinNumber")}</span>
                                <span className="font-medium text-gray-900">
                                  {vehicle?.vinNumber ? vehicle?.vinNumber.length > 5 ? "************" + vehicle?.vinNumber.slice(-5) : vehicle?.vinNumber : "N/A"}
                                </span>
                              </div>
        
                              <div>
                                <span className="text-gray-500 block">{translate("wishList.vehicle.currentBid")}</span>
                                <span className="font-semibold text-orange-600">
                                  {formatPrice(vehicle?.latestBid || vehicle?.startingBid )}
                                </span>
                              </div>
                              <div>
                                <span className="text-gray-500 block">{translate("bidInfo.labels.marketValue")}</span>
                                <span className="font-medium text-gray-900">
                                  {formatPrice(vehicle?.marketValue || 0)}
                                </span>
                              </div>
                              <div>
                                <span className="text-gray-500 block">{translate("wishList.vehicle.auction")}</span>
                                <div className="font-medium text-gray-900 space-y-1">
                                  <div>{vehicle?.auctionLanesVehicles?.[0]?.auctionLane?.auction?.name || "-"}</div>
                                  <div className="text-gray-600 text-xs">
                                    {translate("list.labels.laneNumber")} {vehicle?.auctionLanesVehicles?.[0]?.auctionLane?.lane?.name || "-"}
                                  </div>
                                  <div className="text-gray-600 text-xs">
                                    {translate("vehicleCard.labels.runNumber")} {vehicle?.auctionLanesVehicles?.[0]?.runNo || "-"}
                                  </div>
                                </div>
                              </div>
                              <div>
                                <span className="text-gray-500 block">{translate("wonVehicles.labels.status")}</span>
                                <span className="font-medium text-gray-900">
                                  {vehicle?.auctionLanesVehicles?.[0]?.auctionLane?.auctionStatus?.name || "-"}
                                </span>
                              </div>
                             
                             
                            </div>
                          </div>

                          {/* Action Buttons */}
                          <div className="flex flex-col sm:flex-row lg:flex-col space-y-1 sm:space-y-0 sm:space-x-2 lg:space-x-0 lg:space-y-1 lg:w-28">
                            <button
                              onClick={() => navigate(`/${vehicle?.slug}`)}
                              className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors text-xs font-medium"
                            >
                              {translate("wishList.vehicle.viewDetails")}
                            </button>
                            
                            <button
                              onClick={() => removeFromWishlist(vehicle?.id)}
                              className="px-3 py-1.5 bg-orange-500 text-white rounded-md hover:bg-orange-600 transition-colors text-xs font-medium"
                            >
                              {translate("wishList.vehicle.removeFromWishlist")}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
</>
                );
              })}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-700">
                  {translate("wishList.pagination.showing")} {startIndex + 1} {translate("wishList.pagination.to")} {Math.min(endIndex, pagination.total)} {translate("wishList.pagination.of")} {pagination.total} {translate("wishList.pagination.items")}
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {translate("wishList.pagination.previous")}
                  </button>

                  <div className="flex space-x-1">
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                      <button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        className={`px-3 py-2 text-sm font-medium rounded-md ${
                          currentPage === page
                            ? "bg-orange-500 text-white"
                            : "text-gray-700 bg-white border border-gray-300 hover:bg-gray-50"
                        }`}
                      >
                        {page}
                      </button>
                    ))}
                  </div>

                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {translate("wishList.pagination.next")}
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </motion.div>
    </>
  );
};

export default WishList;
